import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { toast } from 'react-hot-toast';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';

import { SettingsProps } from '@pages/types';
import { useRetryOvo } from '@pages/hooks/useRetryOvo';

interface RetryOvoButtonProps {
  settings: SettingsProps;
  referenceId?: string;
  onRetried?: () => void;
}

const RETRY_COOLDOWN_SECONDS = 65;
const STORAGE_PREFIX = 'ovo_retry_available_at:';

const readAvailableAt = (key: string): number => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? Number(stored) || 0 : 0;
  } catch {
    return 0;
  }
};

const writeAvailableAt = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
};

const secondsUntil = (timestamp: number): number => {
  if (!timestamp) return 0;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
};

const showRetrySentToast = () => {
  toast.custom(
    (t) => (
      <div
        className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-primary-dark bg-primary-main p-5 text-white shadow-lg transition-opacity"
        style={{ opacity: t.visible ? 1 : 0 }}
      >
        <IconCircleCheck className="mt-0.5 h-7 w-7 flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <p className="text-base font-bold">Notifikasi pembayaran terkirim</p>
          <p className="text-sm text-white/90">
            Permintaan pembayaran berhasil dikirim ulang. Silakan lanjutkan
            pembayaran melalui aplikasi OVO.
          </p>
        </div>
      </div>
    ),
    { position: 'bottom-right', duration: 5000 }
  );
};

const showRetryErrorToast = () => {
  toast.custom(
    (t) => (
      <div
        className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-error-main bg-error-main p-5 text-white shadow-lg transition-opacity"
        style={{ opacity: t.visible ? 1 : 0 }}
      >
        <IconCircleX className="mt-0.5 h-7 w-7 flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <p className="text-base font-bold">Gagal mengirim ulang pembayaran</p>
          <p className="text-sm text-white/90">
            Kami tidak dapat mengirim ulang permintaan pembayaran OVO saat ini.
            Silakan coba lagi dalam beberapa saat.
          </p>
        </div>
      </div>
    ),
    { position: 'bottom-right', duration: 5000 }
  );
};

const RetryOvoButton: React.FC<RetryOvoButtonProps> = ({
  settings,
  referenceId,
  onRetried,
}) => {
  const location = useLocation();
  const storageKey = useMemo(() => {
    const { id } = qs.parse(location.search, { ignoreQueryPrefix: true }) as {
      id?: string;
    };
    return `${STORAGE_PREFIX}${id || 'unknown'}`;
  }, [location.search]);

  const [secondsLeft, setSecondsLeft] = useState(0);

  // On first arrival at the redirect step the OVO push has just been sent, so
  // the button starts on cooldown. The deadline is persisted so a refresh keeps
  // counting down instead of resetting.
  useEffect(() => {
    let availableAt = readAvailableAt(storageKey);
    if (!availableAt) {
      availableAt = Date.now() + RETRY_COOLDOWN_SECONDS * 1000;
      writeAvailableAt(storageKey, availableAt);
    }
    setSecondsLeft(secondsUntil(availableAt));
  }, [storageKey]);

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const interval = setInterval(() => {
      setSecondsLeft(secondsUntil(readAvailableAt(storageKey)));
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft, storageKey]);

  const { mutate: retryOvo, isPending } = useRetryOvo(referenceId, () => {
    const availableAt = Date.now() + RETRY_COOLDOWN_SECONDS * 1000;
    writeAvailableAt(storageKey, availableAt);
    setSecondsLeft(secondsUntil(availableAt));
    showRetrySentToast();
    onRetried?.();
  });

  const handleRetry = () => {
    retryOvo(undefined, {
      onError: () => {
        showRetryErrorToast();
      },
    });
  };

  const isDisabled = secondsLeft > 0 || isPending;

  return (
    <div className="flex flex-col gap-1">
      {secondsLeft > 0 && (
        <span className="text-sm text-gray-500">
          Kirim ulang dalam{' '}
          <span className="text-xl font-bold text-error-main">
            0:{String(secondsLeft).padStart(2, '0')}
          </span>
        </span>
      )}
      <button
        onClick={handleRetry}
        disabled={isDisabled}
        className="w-full rounded-lg px-6 py-4 font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundColor: settings.button_color
            ? settings.button_color
            : '#5D87FF',
          color: settings.button_text_color
            ? settings.button_text_color
            : '#FFFFFF',
        }}
      >
        Retry
      </button>
    </div>
  );
};

export default RetryOvoButton;
